import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Plus } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { useContracts, type ContractListParams } from '@/api/contracts';
import { ContractListSection } from './components/contract-list-section';
import { ContractStatBand } from './components/contract-stat-band';
import { customerContractColumns } from './list-configs/contract-column-definitions';
import { customerContractFilters } from './list-configs/contract-filter-fields';
import { ContractOverviewTab } from './overview/contract-overview-tab';

const TAB_VALUES = ['list', 'overview'] as const;
type TabValue = (typeof TAB_VALUES)[number];

/**
 * Contracts — the ported DataCentral v4 customer (sell-side) contract manager.
 * A `PageHeader` (title + link to Partner Contracts + Add) over a 6-card stat
 * band (`ContractStatBand`) and two tabs: **List** (server-paginated table with
 * search/filters/column visibility) and **Overview** (kanban by status + export
 * overdue). Active tab persisted in `?tab=` search param.
 */
export default function ContractsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listParams, setListParams] = useState<ContractListParams>({});

  const requestedTab = searchParams.get('tab') as TabValue | null;
  const activeTab: TabValue =
    requestedTab && (TAB_VALUES as readonly string[]).includes(requestedTab)
      ? requestedTab
      : 'list';

  const onTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container size="full">
      <PageHeader
        title="Contracts"
        description="Customer contracts across their lifecycle — draft, active, renewal and expiry."
        style={{ marginBottom: 'var(--vn-space-4)' }}
        actions={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              startIcon={<Building2 size={15} />}
              onClick={() => navigate('/contracts/partners')}
            >
              Partner contracts
            </Button>
            <Button startIcon={<Plus size={15} />} onClick={() => navigate('/contracts/new')}>
              Add contract
            </Button>
          </div>
        }
      />

      <ContractStatBand params={listParams} />

      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList>
          <TabsTrigger value="list">List</TabsTrigger>
          <TabsTrigger value="overview">Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          {activeTab === 'list' ? (
            <ContractListSection
              useListQuery={useContracts}
              columns={customerContractColumns}
              getRowId={(r) => String(r.contract_id)}
              onRowClick={(r) => navigate(`/contracts/${r.contract_id}`)}
              filterFields={customerContractFilters}
              searchPlaceholder="Search contracts…"
              onParamsChange={setListParams}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="overview">
          {activeTab === 'overview' ? <ContractOverviewTab /> : null}
        </TabsContent>
      </Tabs>
    </Container>
  );
}
